/**
 * What the crowns of the realm believe.
 *
 * A confession is the map's second axis. Without one, the only question a nation
 * can ask about another is "are they winning", and a realm whose whole politics
 * is arithmetic reads as weather rather than as neighbours. With one, two powers
 * can be natural friends while one of them is losing, and two powers that need
 * each other can still be unable to stand in the same room.
 *
 * Six faiths, which is more than the eight nations strictly need and fewer than
 * thirty kingdoms will want to share. That is deliberate: a faith held by exactly
 * one nation is a personality trait, not a religion, and a faith held by all of
 * them is wallpaper. Two pairs share a confession here on purpose — the Ashen
 * Crown and the Gilded Vigil keep the same fire, the Gutter Congregation and the
 * Moonbound listen to the same water — because a shared faith between two powers
 * who are otherwise nothing alike is the most interesting thing this table can
 * produce.
 *
 * **Provinces have no confession.** EU4's religion lives on the map, is converted
 * province by province, and drives unrest and tax. That is cut here, and the
 * reason is in `provinces.ts` not being ours to change and in the honest fact
 * that a per-province faith needs a missionary system, a conversion clock and a
 * tolerance number before it does anything a player can feel. A confession in
 * this game is a property of a *crown*: it decides who will treat with whom, and
 * what a war can be declared about. That is the half that earns its place.
 */

export interface ConfessionDef {
  id: string;
  /** What the faith calls itself. */
  name: string;
  /** What one of its faithful is called. */
  adherent: string;
  /** One line, in its own voice. */
  creed: string;
  /** Banner tint for the diplomacy panel. */
  colour: number;
}

export const CONFESSIONS: ConfessionDef[] = [
  {
    id: 'hearth',
    name: 'The Kept Hearth',
    adherent: 'Hearthkeeper',
    creed: 'The fire was handed to us lit. We have not let it go out. That is the whole of it.',
    colour: 0xd8a24a,
  },
  {
    id: 'litany',
    name: 'The Pale Litany',
    adherent: 'Litanist',
    creed: 'Nobody leaves. We simply stop asking them to work.',
    colour: 0xbcc4cf,
  },
  {
    id: 'oldblood',
    name: 'The Old Blood',
    adherent: 'Oathsworn',
    creed: 'An oath is a debt and a debt is collected. Ask the hills who they belong to.',
    colour: 0x9d3b2f,
  },
  {
    id: 'covenant',
    name: 'The Green Covenant',
    adherent: 'Covenanter',
    creed: 'Cut it and it grows. Burn it and it grows. You are the season, not the wood.',
    colour: 0x4f7a43,
  },
  {
    id: 'deepstone',
    name: 'The Deep Stone',
    adherent: 'Stonekin',
    creed: 'Everything above ground is a rumour. We keep the records because nobody else can read them.',
    colour: 0x6a6f7d,
  },
  {
    id: 'drowned',
    name: 'The Drowned Word',
    adherent: 'Listener',
    creed: 'It is still speaking. You have simply never been quiet enough.',
    colour: 0x3d6b78,
  },
];

export const CONFESSION_COUNT = CONFESSIONS.length;

const BY_ID = new Map<string, number>();
CONFESSIONS.forEach((def, i) => BY_ID.set(def.id, i));

export function confessionIndex(id: string): number {
  const i = BY_ID.get(id);
  return i === undefined ? 0 : i;
}

export function confessionDef(i: number): ConfessionDef {
  return CONFESSIONS[i] ?? CONFESSIONS[0];
}

/**
 * How badly two faiths get on: 0 is indifference, 1 is irreconcilable.
 *
 * Listed as pairs rather than written out as a grid, because a grid of thirty-six
 * numbers is a thing nobody ever reads and therefore a thing nobody ever
 * notices is wrong. Anything unlisted is `DEFAULT_HOSTILITY` — a mild mutual
 * suspicion, which is the right resting state for a world this cold.
 */
const DEFAULT_HOSTILITY = 0.35;

const HOSTILE_PAIRS: Array<[string, string, number]> = [
  // The Hearth burns its dead. The Litany does not consider them finished.
  ['hearth', 'litany', 1],
  ['hearth', 'drowned', 0.8],
  ['hearth', 'oldblood', 0.55],
  ['hearth', 'covenant', 0.3],
  ['hearth', 'deepstone', 0.2],
  // A faith of endless patience against a faith of endless renewal.
  ['litany', 'covenant', 0.9],
  ['litany', 'deepstone', 0.7],
  ['litany', 'oldblood', 0.6],
  ['litany', 'drowned', 0.35],
  ['oldblood', 'covenant', 0.7],
  ['oldblood', 'drowned', 0.5],
  ['oldblood', 'deepstone', 0.5],
  ['covenant', 'drowned', 0.75],
  ['covenant', 'deepstone', 0.6],
  ['deepstone', 'drowned', 0.6],
];

const HOSTILITY: number[][] = CONFESSIONS.map((_, i) =>
  CONFESSIONS.map((_, j) => (i === j ? 0 : DEFAULT_HOSTILITY)),
);
for (const [a, b, v] of HOSTILE_PAIRS) {
  const i = confessionIndex(a);
  const j = confessionIndex(b);
  HOSTILITY[i][j] = v;
  HOSTILITY[j][i] = v;
}

/** 0..1. Zero between two crowns of the same faith, by construction. */
export function confessionHostility(a: number, b: number): number {
  return HOSTILITY[a]?.[b] ?? DEFAULT_HOSTILITY;
}

/**
 * Which faith a nation keeps.
 *
 * A table by faction, because a faction is the thing that already carries a
 * culture in this project — and a deterministic fallback by name, because the
 * realm is about to grow from eight kingdoms to thirty and a crown with no faith
 * at all would be a hole in every calculation below. The fallback is a hash of
 * the nation's own id: stable across runs, stable across saves, and different for
 * two nations that happen to share a roster.
 */
const BY_FACTION: Record<string, string> = {
  ashen: 'hearth',
  vigil: 'hearth',
  litany: 'litany',
  ironfang: 'oldblood',
  verdant: 'covenant',
  karrun: 'deepstone',
  gutter: 'drowned',
  moonbound: 'drowned',
};

export function confessionFor(factionId: string, nationId: string): number {
  const named = BY_FACTION[factionId];
  if (named !== undefined) return confessionIndex(named);
  // FNV-1a, written out rather than imported, so this table has no dependencies
  // at all and can be read on its own.
  let h = 2166136261 >>> 0;
  for (let i = 0; i < nationId.length; i++) {
    h ^= nationId.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h % CONFESSION_COUNT;
}
